import {
  createSlice,
  createAsyncThunk,
  miniSerializeError,
  SerializedError,
} from "@reduxjs/toolkit";
import { LOADING_STATE, ERGONA_SME_USER } from "src/constants/constants";
import authService from "src/services/authService";
import redirectService from "src/services/redirectService";

const AUTHORIZED_LDAP_GROUPS: { [key: string]: string[] } = {
  "us-east-1": ["ergona-user"],
  "eu-west-1": ["ergona-user-eu"],
  "ap-northeast-1": ["ergona-user-fe"],
};
const AUTHORIZED_JOB_ROTATION_GROUP = "ergona-job-rotation-user";

export type UserState = {
  token: string;
  alias: string;
  lastName: string;
  firstName: string;
  email: string;
  ldap: string[];
  country: string;
  homeSite: string;
  userLoading: LOADING_STATE;
  authError: SerializedError | null;
  isAuthorized: boolean;
  isJobRotationUser: boolean;
  isErgonaSMEUser: boolean;
};

const initialState: UserState = {
  token: "",
  alias: "",
  lastName: "",
  firstName: "",
  email: "",
  country: "",
  homeSite: "",
  ldap: [],
  userLoading: "pending",
  authError: null,
  isAuthorized: false,
  isJobRotationUser: false,
  isErgonaSMEUser: false,
};

/**
 * get the authenticated user
 * @returns {Object}
 */
export const getUser = createAsyncThunk("user/getUser", async () => {
  const shouldRefreshAccessAndSecretKeys = false;
  return authService.login(shouldRefreshAccessAndSecretKeys);
});

export const redirectToRegionSite = createAsyncThunk(
  "user/redirectToRegionSite",
  async (country: string) => {
    redirectService.redirect(country);
  }
);

/** User Slice */
const { reducer, actions } = createSlice({
  name: "user",
  initialState,
  reducers: {
    getUserFailure: (state) => {
      state.userLoading = "rejected";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUser.pending, (state) => {
      state.userLoading = "pending";
    });
    /** Add reducers for additional action types here, and handle loading state as needed */
    builder.addCase(getUser.fulfilled, (state, { payload }) => {
      const {
        alias,
        token,
        email,
        firstName,
        lastName,
        ldap,
        country,
        homeSite,
      } = payload;
      state.token = token;
      state.alias = alias;
      state.email = email;
      state.firstName = firstName;
      state.lastName = lastName;
      state.ldap = ldap;
      state.country = country;
      state.homeSite = homeSite;
      state.isAuthorized = (
        AUTHORIZED_LDAP_GROUPS["us-east-1"] ||
        AUTHORIZED_LDAP_GROUPS["us-east-1"]
      ).some((authorizedGroup) => state.ldap?.includes(authorizedGroup));
      state.isJobRotationUser = state.ldap?.includes(
        AUTHORIZED_JOB_ROTATION_GROUP
      );
      state.isErgonaSMEUser = state.ldap?.includes(ERGONA_SME_USER);
      state.userLoading = "fulfilled";
    });
    builder.addCase(getUser.rejected, (state, { error }) => {
      console.log("getUser rejected state: ", state);
      console.log("getUser rejected error: ", error);
      state.authError = miniSerializeError(error);
      state.userLoading = "rejected";
    });
  },
});

export const {} = actions;

export default reducer;
